import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import API from '../../../API';
import Item from './Item';
import ProgressOverlay from '../../overlays/ProgressOverlay';
import ShippingNotice from '../ShippingNotice';
import { useTenantPath } from '../../../utils/tenantPath';

// Shared layout for the Best Sellers and Hot Sellers landing pages.
// Both pages render one top item per leaf category, sold-DESC. The
// only differences between the two are the title and which API
// endpoint is hit; everything visual (item card, grid spacing,
// shipping notice, breadcrumb) is identical to NewArrivalsPanel.
type Mode = 'best-sellers' | 'hot-sellers';

type Props = {
  mode: Mode;
};

const TITLES: Record<Mode, string> = {
  'best-sellers': 'Best Sellers',
  'hot-sellers': 'Hot Sellers',
};

const fetchFor = (mode: Mode, store: string) =>
  mode === 'best-sellers'
    ? API.getBestSellersByLeaf(store)
    : API.getHotSellersByLeaf(store);

const TopSellersPanel = ({ mode }: Props) => {
  const { store } = useParams<{ store: string }>();
  const path = useTenantPath();
  const [loading, setLoading] = useState<boolean>(true);
  const [items, setItems] = useState<IItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { items: fetched } = await fetchFor(mode, store);
        if (cancelled) return;
        setItems(fetched);
      } catch (err: any) {
        console.error(`TopSellersPanel (${mode}) fetch failed:`, err?.message ?? err);
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, store]);

  const title = TITLES[mode];

  return (
    <ProgressOverlay enabled={loading}>
      <div className="infobar">
        <div className="breadcrumb">
          &nbsp;&nbsp; <span><Link to={path.category()}>Categories</Link></span>
          &nbsp;&nbsp; &rsaquo;
          &nbsp;&nbsp; <span>{title}</span>
        </div>
        <div className="spacer" />
        <ShippingNotice />
      </div>
      {!loading && items.length === 0 && (
        <Typography
          variant="body2"
          sx={{ padding: '2rem 1rem', textAlign: 'center', color: '#64748b' }}
        >
          No {title.toLowerCase()} yet — check back soon.
        </Typography>
      )}
      <Grid container justifyContent="center" spacing={2} className="items">
        {items.map((item) => (
          <Grid key={item.id} item>
            <Item item={item} />
          </Grid>
        ))}
      </Grid>
    </ProgressOverlay>
  );
};

export default TopSellersPanel;
